import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenant } from '../../contexts/TenantContext';
import { useAuth } from '../../contexts/AuthContext';
import { useIdentityReveal } from '../../contexts/IdentityRevealContext';
import { useRealtimeJobs, type RealtimeJD } from '../../hooks/useRealtimeJobs';
import { useRealtimeResumes } from '../../hooks/useRealtimeResumes';
import { useAuthFetch } from '../../hooks/useAuthFetch';
import { isOrgAdmin } from '../../utils/ownership';
import { DEFAULT_RADAR_DIMENSION_WEIGHTS } from '../../utils/radarDimensions';
import { timeAgo } from '../../utils/timeAgo';
import JobCandidateRadar from '../../components/charts/JobCandidateRadar';
import './DashboardPage.css';

type RoleCandidate = {
  resumeId: string;
  displayName: string;
  score: number | null;
  rank: number;
  status?: string;
  uploadedAt?: string;
  dimensionScores?: Record<string, number>;
  analysis?: string;
};

type JobDiagnosticsState = {
  loading: boolean;
  error: string | null;
  candidates: RoleCandidate[];
  discoverTop?: RoleCandidate;
};

type DiscoverRanking = {
  resumeId?: string;
  displayName?: string;
  resumeFilename?: string;
  filename?: string;
  score?: number | string | null;
  rank?: number;
  dimensionScores?: Record<string, number>;
  summary?: string;
  explanation?: string;
};

const JOB_CARD_RADAR_PALETTE: Array<{ fill: string; stroke: string }> = [
  { fill: 'rgba(59, 130, 246, 0.08)', stroke: 'rgba(30, 64, 175, 0.88)' },
  { fill: 'rgba(249, 115, 22, 0.08)', stroke: 'rgba(194, 65, 12, 0.88)' },
  { fill: 'rgba(16, 185, 129, 0.08)', stroke: 'rgba(6, 95, 70, 0.9)' },
];

const DETAIL_JOB_RADAR = {
  fill: 'rgba(22, 163, 74, 0.2)',
  stroke: 'rgba(22, 163, 74, 0.95)',
};

const DETAIL_CANDIDATE_RADAR = {
  fill: 'rgba(59, 130, 246, 0.22)',
  stroke: 'rgba(37, 99, 235, 0.95)',
};

const TIER_RANK: Record<string, number> = {
  free: 0,
  starter: 1,
  pro: 2,
  business: 3,
  enterprise: 4,
};

const toTimestamp = (value: string): number => {
  const parsed = Date.parse(value || '');
  return Number.isFinite(parsed) ? parsed : 0;
};

const shortText = (value: string, max = 96): string => {
  const text = (value || '').trim();
  if (!text) return 'No context provided';
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
};

const formatMemberScopeLabel = (member: { displayName?: string; email?: string }): string => {
  const name = (member.displayName || '').trim();
  const email = (member.email || '').trim();
  if (name && email) return `${name} (${email})`;
  return name || email || 'Unknown member';
};

const coerceScore = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const normalizeDimensionScores = (value: unknown): Record<string, number> | undefined => {
  if (!value || typeof value !== 'object') return undefined;
  const source = value as Record<string, unknown>;
  const out: Record<string, number> = {};
  Object.entries(source).forEach(([key, raw]) => {
    if (typeof raw === 'number' && Number.isFinite(raw)) {
      out[key] = raw;
      return;
    }
    if (typeof raw === 'string') {
      const parsed = Number(raw);
      if (Number.isFinite(parsed)) out[key] = parsed;
    }
  });
  return Object.keys(out).length > 0 ? out : undefined;
};

const fallbackAlias = (resumeId: string): string => `Candidate ${(resumeId || 'unknown').slice(0, 8).toUpperCase()}`;

const DEFAULT_CHART_DIMENSION_WEIGHTS: Record<string, number> = {
  ...DEFAULT_RADAR_DIMENSION_WEIGHTS,
};

const resolveChartDimensionWeights = (weights?: Record<string, number>): Record<string, number> => {
  if (!weights || Object.keys(weights).length === 0) {
    return { ...DEFAULT_CHART_DIMENSION_WEIGHTS };
  }

  const merged = { ...DEFAULT_CHART_DIMENSION_WEIGHTS };
  let hasPositive = false;
  Object.entries(weights).forEach(([key, value]) => {
    if (!(key in merged)) return;
    const numeric = Number(value);
    if (!Number.isFinite(numeric) || numeric < 0) return;
    merged[key] = numeric;
    if (numeric > 0) hasPositive = true;
  });

  if (!hasPositive) return { ...DEFAULT_CHART_DIMENSION_WEIGHTS };
  const total = Object.values(merged).reduce((sum, v) => sum + v, 0);
  if (total <= 0) return { ...DEFAULT_CHART_DIMENSION_WEIGHTS };

  const normalized: Record<string, number> = {};
  Object.entries(merged).forEach(([key, value]) => {
    normalized[key] = value / total;
  });
  return normalized;
};

const summarizeAnalysis = (value?: string): string | null => {
  const text = (value || '').replace(/\s+/g, ' ').trim();
  if (!text) return null;
  if (text.length <= 220) return text;
  return `${text.slice(0, 219)}…`;
};

const mapDiscoverTopCandidate = (candidate: DiscoverRanking | undefined): RoleCandidate | undefined => {
  if (!candidate?.resumeId) return undefined;
  return {
    resumeId: candidate.resumeId,
    displayName: candidate.displayName || candidate.resumeFilename || candidate.filename || fallbackAlias(candidate.resumeId),
    score: coerceScore(candidate.score),
    rank: typeof candidate.rank === 'number' ? candidate.rank : 1,
    dimensionScores: normalizeDimensionScores(candidate.dimensionScores),
    analysis: candidate.summary || candidate.explanation,
  };
};

const statusLabel = (status: RealtimeJD['status']): string => {
  if (status === 'filled') return 'Filled';
  if (status === 'closed') return 'Closed';
  return 'Open';
};

export const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const authFetch = useAuthFetch();
  const { org, role } = useTenant();
  const { user } = useAuth();
  const { revealIdentity } = useIdentityReveal();
  const { jobs: allJobs, loading: jobsLoading } = useRealtimeJobs(org?.orgId);
  const { resumes: allResumes, loading: resumesLoading } = useRealtimeResumes(org?.orgId);
  const [jobDiagnostics, setJobDiagnostics] = useState<Record<string, JobDiagnosticsState>>({});
  const [selectedCandidateByJob, setSelectedCandidateByJob] = useState<Record<string, string>>({});
  const fetchedDiagnosticsRef = useRef<Set<string>>(new Set());
  const backfillTriggeredRef = useRef(false);

  // Scope selector — `me` = my pipeline only, `org` = entire organization,
  // any other value = a specific member's uid (admin only).
  // Default: members default to `me`; admins default to `org`.
  const myUid = user?.uid;
  const adminLike = isOrgAdmin(role);
  const [scope, setScope] = useState<string>(() => (adminLike ? 'org' : 'me'));
  const [applyTagFilter, setApplyTagFilter] = useState(false);
  const canUseTagFilter = useMemo(() => {
    const plan = (org?.plan || 'free').toLowerCase();
    return (TIER_RANK[plan] ?? 0) >= 2;
  }, [org?.plan]);

  useEffect(() => {
    if (canUseTagFilter) return;
    setApplyTagFilter(false);
  }, [canUseTagFilter]);

  // Re-default the scope when role finishes loading (in case the
  // initial render happened before `role` hydrated from the backend).
  const scopeInitializedRef = useRef(false);
  useEffect(() => {
    if (scopeInitializedRef.current) return;
    if (role === null) return;
    scopeInitializedRef.current = true;
    setScope(adminLike ? 'org' : 'me');
  }, [role, adminLike]);

  // If a previously-selected member is no longer in the org, fall back to org view.
  useEffect(() => {
    if (scope === 'me' || scope === 'org') return;
    const stillExists = (org?.members ?? []).some(m => m.userId === scope);
    if (!stillExists) setScope(adminLike ? 'org' : 'me');
  }, [scope, org?.members, adminLike]);

  const effectiveScopeUid = useMemo<string | null>(() => {
    if (scope === 'org') return null;
    if (scope === 'me') return myUid ?? null;
    return scope; // specific member uid
  }, [scope, myUid]);

  // Apply the scope filter once, up-front. All downstream calculations
  // (active jobs, fill rate, alignment cards, etc.) operate on these
  // already-scoped arrays and require no further changes.
  const jobs = useMemo<RealtimeJD[]>(() => {
    if (!effectiveScopeUid) return allJobs;
    return allJobs.filter(j => j.uploadedBy === effectiveScopeUid);
  }, [allJobs, effectiveScopeUid]);

  const resumes = useMemo(() => {
    if (!effectiveScopeUid) return allResumes;
    return allResumes.filter(r => r.uploadedBy === effectiveScopeUid);
  }, [allResumes, effectiveScopeUid]);

  const scopeLabel = useMemo(() => {
    if (scope === 'org') return 'across organization';
    if (scope === 'me') return 'for me';
    const member = (org?.members ?? []).find(m => m.userId === scope);
    if (!member) return 'for selected member';
    return `for ${formatMemberScopeLabel(member)}`;
  }, [scope, org?.members]);

  // Job status metrics (quarterly)
  const now = new Date();
  const quarterStart = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
  const quarterLabel = `Q${Math.floor(now.getMonth() / 3) + 1} ${now.getFullYear()}`;

  const activeJobs = jobs.filter(j => j.status === 'open');
  const filledThisQuarter = jobs.filter(j => j.status === 'filled' && j.closedAt && new Date(j.closedAt) >= quarterStart);
  const closedThisQuarter = jobs.filter(j => j.status === 'closed' && j.closedAt && new Date(j.closedAt) >= quarterStart);
  const totalClosedQuarter = filledThisQuarter.length + closedThisQuarter.length;
  const fillRate = totalClosedQuarter > 0 ? Math.round((filledThisQuarter.length / totalClosedQuarter) * 100) : 0;
  const enrichedResumes = resumes.filter(r => {
    const status = (r.status || '').toLowerCase();
    if (status === 'indexed' || status === 'enriched') return true;
    return !!r.inlineScores && Object.keys(r.inlineScores).length > 0;
  }).length;

  const recentJobs = useMemo(() => {
    const sorted = [...jobs].sort((a, b) => toTimestamp(b.createdAt) - toTimestamp(a.createdAt));
    return sorted.slice(0, 3);
  }, [jobs]);

  useEffect(() => {
    if (!org?.orgId || backfillTriggeredRef.current) return;
    const key = `xrank_dashboard_prefetch_backfill_v1:${org.orgId}`;
    if (localStorage.getItem(key) === 'done') {
      backfillTriggeredRef.current = true;
      return;
    }

    backfillTriggeredRef.current = true;
    authFetch('/api/match-prefetch/backfill', {
      method: 'POST',
      body: JSON.stringify({ top_n: 10, scope: 'all', limit: 200 }),
    })
      .then(() => localStorage.setItem(key, 'done'))
      .catch(() => {
        // Retry on next page refresh if this one fails.
        backfillTriggeredRef.current = false;
      });
  }, [authFetch, org?.orgId]);

  useEffect(() => {
    const visibleIds = new Set(recentJobs.map(job => job.jdId));
    setJobDiagnostics(prev => {
      const next: Record<string, JobDiagnosticsState> = {};
      visibleIds.forEach(id => {
        if (prev[id]) next[id] = prev[id];
      });
      return next;
    });
  }, [recentJobs]);

  useEffect(() => {
    fetchedDiagnosticsRef.current.clear();
    setJobDiagnostics(prev => {
      const next: Record<string, JobDiagnosticsState> = {};
      Object.entries(prev).forEach(([jdId, value]) => {
        next[jdId] = { ...value, discoverTop: undefined };
      });
      return next;
    });
  }, [applyTagFilter]);

  useEffect(() => {
    if (!org?.orgId) return;
    let cancelled = false;

    const buildLocalCandidates = (job: RealtimeJD): RoleCandidate[] => {
      return resumes
        .filter(resume => (resume.jdIds ?? []).includes(job.jdId))
        .map(resume => {
          const score = resume.inlineScores?.[job.jdId]?.score;
          const defaultAlias = fallbackAlias(resume.resumeId);
          const displayName = revealIdentity
            ? (resume.displayName || resume.filename || resume.candidateAlias || defaultAlias)
            : (resume.candidateAlias || defaultAlias);
          return {
            resumeId: resume.resumeId,
            displayName,
            score: typeof score === 'number' ? score : null,
            rank: 0,
            status: resume.status,
            uploadedAt: resume.uploadedAt,
            dimensionScores: normalizeDimensionScores(resume.inlineScores?.[job.jdId]?.dimensionScores),
            analysis: resume.inlineScores?.[job.jdId]?.summary,
          } satisfies RoleCandidate;
        })
        .sort((a, b) => (b.score ?? -1) - (a.score ?? -1))
        .map((candidate, index) => ({ ...candidate, rank: index + 1 }));
    };

    const fetchForJob = async (job: RealtimeJD) => {
      const localCandidates = buildLocalCandidates(job);
      setJobDiagnostics(prev => ({
        ...prev,
        [job.jdId]: {
          loading: true,
          error: null,
          candidates: localCandidates.length > 0 ? localCandidates : (prev[job.jdId]?.candidates ?? []),
          discoverTop: prev[job.jdId]?.discoverTop,
        },
      }));

      const fetchDiscoverTopCandidate = async (): Promise<RoleCandidate | undefined> => {
        const tagFilterEnabled = canUseTagFilter && applyTagFilter;
        const cacheKey = `xrank:dashboard:discoverTop:${job.jdId}:${scope}:${tagFilterEnabled ? 'tag-on' : 'tag-off'}`;
        if (tagFilterEnabled) {
          const cachedRaw = sessionStorage.getItem(cacheKey);
          if (cachedRaw) {
            try {
              return JSON.parse(cachedRaw) as RoleCandidate;
            } catch {
              sessionStorage.removeItem(cacheKey);
            }
          }
        }

        try {
          const discoverRes = await authFetch('/api/match', {
            method: 'POST',
            body: JSON.stringify({
              jd_id: job.jdId,
              scope: 'all',
              top_n: 1,
              apply_tag_filter: tagFilterEnabled,
            }),
          });
          const discoverPayload = await discoverRes.json();

          if (discoverRes.status === 200 && Array.isArray(discoverPayload?.rankings)) {
            const mapped = mapDiscoverTopCandidate(discoverPayload.rankings[0] as DiscoverRanking | undefined);
            if (tagFilterEnabled && mapped) {
              sessionStorage.setItem(cacheKey, JSON.stringify(mapped));
            }
            return mapped;
          }

          if (discoverRes.status === 202 && discoverPayload?.matchId) {
            const deadline = Date.now() + 15000;
            while (Date.now() < deadline) {
              await new Promise<void>(resolve => window.setTimeout(resolve, 900));
              const pollRes = await authFetch(`/api/match-jobs/${discoverPayload.matchId}`);
              if (!pollRes.ok) continue;
              const pollPayload = await pollRes.json();
              if (pollPayload?.status === 'failed') break;
              const fromPoll = mapDiscoverTopCandidate(
                Array.isArray(pollPayload?.rankings) ? pollPayload.rankings[0] as DiscoverRanking : undefined,
              );
              if (fromPoll) {
                if (tagFilterEnabled) {
                  sessionStorage.setItem(cacheKey, JSON.stringify(fromPoll));
                }
                return fromPoll;
              }
            }
          }
        } catch {
          // Keep role diagnostics available even if discover top-1 fetch fails.
        }
        return undefined;
      };

      try {
        const res = await authFetch(`/api/job-descriptions/${job.jdId}/candidates`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const payload = await res.json() as {
          candidates?: Array<{
            resumeId?: string;
            displayName?: string;
            filename?: string;
            score?: number | string | null;
            rank?: number;
            status?: string;
            uploadedAt?: string;
            dimensionScores?: Record<string, number>;
            summary?: string;
            explanation?: string;
          }>;
        };

        const mapped = (payload.candidates ?? [])
          .filter((candidate): candidate is NonNullable<typeof candidate> & { resumeId: string } => Boolean(candidate.resumeId))
          .map((candidate, index) => ({
            resumeId: candidate.resumeId,
            displayName: candidate.displayName || candidate.filename || fallbackAlias(candidate.resumeId),
            score: coerceScore(candidate.score),
            rank: typeof candidate.rank === 'number' ? candidate.rank : index + 1,
            status: candidate.status,
            uploadedAt: candidate.uploadedAt,
            dimensionScores: normalizeDimensionScores(candidate.dimensionScores),
            analysis: candidate.summary || candidate.explanation,
          }))
          .sort((a, b) => (b.score ?? -1) - (a.score ?? -1))
          .map((candidate, index) => ({ ...candidate, rank: index + 1 }));

        if (cancelled) return;
        setJobDiagnostics(prev => ({
          ...prev,
          [job.jdId]: {
            loading: false,
            error: null,
            candidates: mapped,
            discoverTop: prev[job.jdId]?.discoverTop,
          },
        }));
      } catch {
        if (cancelled) return;
        setJobDiagnostics(prev => ({
          ...prev,
          [job.jdId]: {
            loading: false,
            error: localCandidates.length > 0 ? null : 'Diagnostics unavailable right now',
            candidates: localCandidates,
            discoverTop: prev[job.jdId]?.discoverTop,
          },
        }));
      }

      const discoverTop = await fetchDiscoverTopCandidate();
      if (cancelled || !discoverTop) return;
      setJobDiagnostics(prev => {
        const existing = prev[job.jdId];
        if (!existing) return prev;
        return {
          ...prev,
          [job.jdId]: {
            ...existing,
            discoverTop,
          },
        };
      });
    };

    for (const job of recentJobs) {
      if (fetchedDiagnosticsRef.current.has(job.jdId)) continue;
      fetchedDiagnosticsRef.current.add(job.jdId);
      void fetchForJob(job);
    }

    return () => {
      cancelled = true;
    };
  }, [authFetch, org?.orgId, recentJobs, resumes, revealIdentity, applyTagFilter, canUseTagFilter, scope]);

  const roleTopCandidateByJob = useMemo(() => {
    const map: Record<string, RoleCandidate | undefined> = {};
    for (const job of recentJobs) {
      const diagnostics = jobDiagnostics[job.jdId];
      if (!diagnostics || diagnostics.loading || diagnostics.candidates.length === 0) {
        map[job.jdId] = undefined;
        continue;
      }
      map[job.jdId] = diagnostics.candidates.find(c => typeof c.score === 'number') || diagnostics.candidates[0];
    }
    return map;
  }, [jobDiagnostics, recentJobs]);

  const topCandidateByJob = useMemo(() => {
    const map: Record<string, RoleCandidate | undefined> = {};
    for (const job of recentJobs) {
      const diagnostics = jobDiagnostics[job.jdId];
      map[job.jdId] = diagnostics?.discoverTop || roleTopCandidateByJob[job.jdId];
    }
    return map;
  }, [jobDiagnostics, recentJobs, roleTopCandidateByJob]);

  const candidateOptionsByJob = useMemo(() => {
    const out: Record<string, RoleCandidate[]> = {};
    for (const job of recentJobs) {
      const diagnostics = jobDiagnostics[job.jdId];
      const candidates: RoleCandidate[] = [];
      const seen = new Set<string>();

      const add = (candidate?: RoleCandidate) => {
        if (!candidate?.resumeId || seen.has(candidate.resumeId)) return;
        seen.add(candidate.resumeId);
        candidates.push(candidate);
      };

      add(topCandidateByJob[job.jdId]);
      (diagnostics?.candidates ?? []).forEach(add);
      out[job.jdId] = candidates;
    }
    return out;
  }, [jobDiagnostics, recentJobs, topCandidateByJob]);

  useEffect(() => {
    setSelectedCandidateByJob(prev => {
      const next: Record<string, string> = {};
      for (const job of recentJobs) {
        const options = candidateOptionsByJob[job.jdId] ?? [];
        const previousSelection = prev[job.jdId];
        if (previousSelection && options.some(candidate => candidate.resumeId === previousSelection)) {
          next[job.jdId] = previousSelection;
          continue;
        }
        if (options[0]?.resumeId) {
          next[job.jdId] = options[0].resumeId;
        }
      }
      return next;
    });
  }, [candidateOptionsByJob, recentJobs]);

  return (
    <div className="v2-page xrank-dashboard">
      <div className="v2-page-header xrank-dashboard__header">
        <div>
          <h1>Actionable Intelligence Dashboard</h1>
          <p>Track pipeline velocity, role diagnostics, and candidate quality from one workspace.</p>
        </div>
        <div className="xrank-dashboard__scope">
          <label htmlFor="xrank-scope-select" className="xrank-dashboard__scope-label">View</label>
          <select
            id="xrank-scope-select"
            className="xrank-dashboard__scope-select"
            value={scope}
            onChange={(e) => setScope(e.target.value)}
            aria-label="Filter dashboard by ownership"
          >
            <option value="me">My pipeline</option>
            <option value="org">Organization</option>
            {adminLike && (org?.members ?? [])
              .filter(m => m.userId !== myUid)
              .map(m => (
                <option key={m.userId} value={m.userId}>
                  {formatMemberScopeLabel(m)}
                </option>
              ))}
          </select>
          <label className="xrank-dashboard__tag-toggle">
            <input
              type="checkbox"
              checked={canUseTagFilter && applyTagFilter}
              disabled={!canUseTagFilter}
              onChange={(e) => setApplyTagFilter(e.target.checked)}
            />
            <span>Apply tag constraints (Pro+)</span>
          </label>
        </div>
      </div>

      <div className="xrank-dashboard__kpis">
        <div className="v2-metric-card xrank-dashboard__kpi-card">
          <div className="xrank-dashboard__kpi-label">Active Jobs</div>
          <div className="xrank-dashboard__kpi-value">{jobsLoading ? '—' : activeJobs.length}</div>
          <div className="xrank-dashboard__kpi-sub">Hiring in progress {scopeLabel}</div>
        </div>

        <div className="v2-metric-card xrank-dashboard__kpi-card">
          <div className="xrank-dashboard__kpi-label">Enriched Resumes</div>
          <div className="xrank-dashboard__kpi-value">{resumesLoading ? '—' : enrichedResumes.toLocaleString()}</div>
          <div className="xrank-dashboard__kpi-sub">Ready for semantic matching {scopeLabel}</div>
        </div>

        <div className="v2-metric-card xrank-dashboard__kpi-card">
          <div className="xrank-dashboard__kpi-label">Quarterly Fill Rate</div>
          <div className="xrank-dashboard__kpi-value">{jobsLoading ? '—' : `${fillRate}%`}</div>
          <div className="xrank-dashboard__kpi-sub">{filledThisQuarter.length} / {totalClosedQuarter} roles filled ({quarterLabel}) {scopeLabel}</div>
          <div className="xrank-dashboard__fill-track" aria-hidden>
            <div className="xrank-dashboard__fill-bar" style={{ width: `${fillRate}%` }} />
          </div>
        </div>
      </div>

      <div className="xrank-dashboard__grid">
        <div className="v2-card">
          <div className="v2-card-header">
            <h2>Job-To-Candidate Alignment</h2>
          </div>
          <div className="v2-card-body xrank-dashboard__alignment-list">
            {recentJobs.length === 0 && (
              <div className="v2-empty">
                <h3>No recent jobs</h3>
                <p>Create a job to start seeing diagnostics.</p>
              </div>
            )}

            {recentJobs.map((job, index) => {
              const diagnostics = jobDiagnostics[job.jdId];
              const topMatch = topCandidateByJob[job.jdId];
              const jobRadarWeights = resolveChartDimensionWeights(job.dimensionWeights);
              const radarPalette = JOB_CARD_RADAR_PALETTE[index % JOB_CARD_RADAR_PALETTE.length];
              const hasCandidates = (diagnostics?.candidates.length ?? 0) > 0;
              const candidateOptions = candidateOptionsByJob[job.jdId] ?? [];
              const selectedCandidate = candidateOptions.find(candidate => candidate.resumeId === selectedCandidateByJob[job.jdId]) || candidateOptions[0];
              const showAnalyzing = Boolean(diagnostics?.loading && !selectedCandidate && !hasCandidates);
              const selectedAnalysis = summarizeAnalysis(selectedCandidate?.analysis);
              const handleOpenJob = () => navigate(`/jobs/${job.jdId}`);
              const handleOpenCandidate = () => {
                if (!selectedCandidate?.resumeId) return;
                navigate(`/talent/${selectedCandidate.resumeId}`);
              };

              return (
                <div key={job.jdId} className="xrank-dashboard__alignment-row">
                  <div
                    className="xrank-dashboard__job-item xrank-dashboard__clickable-card"
                    role="button"
                    tabIndex={0}
                    onClick={handleOpenJob}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        handleOpenJob();
                      }
                    }}
                    aria-label={`Open job overview for ${job.title || 'Untitled'}`}
                  >
                    <div className="xrank-dashboard__job-main">
                      <div className="xrank-dashboard__job-copy">
                        <div className="xrank-dashboard__job-topline">
                          <h3 title={job.title || 'Untitled'}>{job.title || 'Untitled'}</h3>
                          <span className={`xrank-dashboard__status xrank-dashboard__status--${job.status}`}>
                            {statusLabel(job.status)}
                          </span>
                        </div>

                        <p className="xrank-dashboard__job-snippet" title={job.snippet || 'No summary available'}>
                          {shortText(job.snippet, 84)}
                        </p>

                        <div className="xrank-dashboard__job-meta">
                          <span>{timeAgo(job.createdAt).label}</span>
                          {showAnalyzing ? (
                            <span className="xrank-dashboard__job-loading"><span className="v2-spinner" /> Analyzing…</span>
                          ) : diagnostics?.error && !hasCandidates && topMatch?.score == null ? (
                            <span className="xrank-dashboard__job-warning">Diagnostics pending</span>
                          ) : (
                            <>
                              <span>{diagnostics?.candidates.length ?? 0} linked</span>
                              {topMatch?.score != null ? (
                                <span className="xrank-dashboard__score-pill">{Math.round(topMatch.score)}% discover #1</span>
                              ) : (
                                <span className="xrank-dashboard__score-pill">Discover pending</span>
                              )}
                            </>
                          )}
                        </div>
                      </div>

                      <div className="xrank-dashboard__job-radar-wrap" aria-hidden>
                        <JobCandidateRadar
                          size={132}
                          showLabels
                          labelVariant="short"
                          labelFontSize={8}
                          dimensionWeights={jobRadarWeights}
                          jobFillColor={radarPalette.fill}
                          jobStrokeColor={radarPalette.stroke}
                          jobStrokeDasharray="none"
                        />
                      </div>
                    </div>
                  </div>

                  <div
                    className={`xrank-dashboard__match-item ${selectedCandidate?.resumeId ? 'xrank-dashboard__clickable-card' : ''}`}
                    role={selectedCandidate?.resumeId ? 'button' : undefined}
                    tabIndex={selectedCandidate?.resumeId ? 0 : undefined}
                    onClick={handleOpenCandidate}
                    onKeyDown={(event) => {
                      if (!selectedCandidate?.resumeId) return;
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        handleOpenCandidate();
                      }
                    }}
                    aria-label={selectedCandidate?.resumeId ? `Open resume workspace for ${selectedCandidate.displayName}` : undefined}
                  >
                    <div className="xrank-dashboard__match-head">
                      <div className="xrank-dashboard__match-score">
                        {selectedCandidate?.score != null ? `${Math.round(selectedCandidate.score)}% Match` : 'Match pending'}
                      </div>
                      <select
                        className="xrank-dashboard__candidate-select"
                        value={selectedCandidate?.resumeId || ''}
                        onChange={event => setSelectedCandidateByJob(prev => ({ ...prev, [job.jdId]: event.target.value }))}
                        onClick={event => event.stopPropagation()}
                        onKeyDown={event => event.stopPropagation()}
                        disabled={candidateOptions.length === 0}
                      >
                        {candidateOptions.length === 0 ? (
                          <option value="">No candidates</option>
                        ) : candidateOptions.map(candidate => (
                          <option key={candidate.resumeId} value={candidate.resumeId}>
                            {candidate.displayName}
                          </option>
                        ))}
                      </select>
                    </div>

                    {showAnalyzing ? (
                      <div className="xrank-dashboard__radar-empty"><span className="v2-spinner" /> Loading candidate profile...</div>
                    ) : selectedCandidate?.dimensionScores ? (
                      <div className="xrank-dashboard__candidate-radar">
                        <JobCandidateRadar
                          size={170}
                          showLabels
                          labelVariant="short"
                          labelFontSize={9}
                          dimensionWeights={jobRadarWeights}
                          dimensionScores={selectedCandidate.dimensionScores}
                          jobFillColor={DETAIL_JOB_RADAR.fill}
                          jobStrokeColor={DETAIL_JOB_RADAR.stroke}
                          candidateFillColor={DETAIL_CANDIDATE_RADAR.fill}
                          candidateStrokeColor={DETAIL_CANDIDATE_RADAR.stroke}
                        />
                      </div>
                    ) : (
                      <div className="xrank-dashboard__radar-empty">Dimension scores unavailable for this candidate.</div>
                    )}

                    {selectedAnalysis && <p className="xrank-dashboard__match-analysis">{selectedAnalysis}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
